import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  Handshake as HandshakeIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import ContractFormDialog from './ContractFormDialog';
import ContractLogSection from './ContractLogSection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contracts-tabpanel-${index}`}
      aria-labelledby={`contracts-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Contract {
  id: string;
  pavadinimas: string;
  sutartiesNumeris: string;
  galiojIki: string;
  suma: number;
  kiekis: number;
  kiekioTipas: 'EUR' | 'Kg' | 'Vienetai' | 'Bilietai' | 'Valandos';
  tipas: 'vidines' | 'partneriai';
  aprasymas?: string;
  statusas: 'aktyvus' | 'pasibaiges' | 'nutrauktas' | 'laukiantis' | 'archyvuotas';
  partneris?: string;
  partnerioKontaktai?: string;
  taikomosNuolaidos?: string;
  sutartymoData?: string;
  responsiblePerson?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  updater?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ContractStats {
  totalContracts: number;
  contractsByType: {
    vidines: number;
    partneriai: number;
  };
  contractsByStatus: {
    aktyvus: number;
    pasibaiges: number;
    nutrauktas: number;
    laukiantis: number;
    archyvuotas?: number;
  };
  expiringContracts: number;
  valueByType: {
    vidines: number;
    partneriai: number;
  };
}

type SortPreset = 'company' | 'newest' | 'expiryAsc' | 'expiryDesc' | 'number' | 'title';

const SORT_PRESET_TO_API: Record<SortPreset, { sortBy: string; sortOrder: string }> = {
  company: { sortBy: 'partneris', sortOrder: 'asc' },
  newest: { sortBy: 'createdAt', sortOrder: 'desc' },
  expiryAsc: { sortBy: 'galiojIki', sortOrder: 'asc' },
  expiryDesc: { sortBy: 'galiojIki', sortOrder: 'desc' },
  number: { sortBy: 'sutartiesNumeris', sortOrder: 'asc' },
  title: { sortBy: 'pavadinimas', sortOrder: 'asc' }
};

function normalizeCompanyGroupKey(partneris?: string | null): string {
  const s = (partneris || '').trim();
  return s.length ? s : '__empty__';
}

/** True if contract is expired/terminated/archived or galiojIki calendar day is before today. */
function isEnded(contract: Contract): boolean {
  if (
    contract.statusas === 'pasibaiges' ||
    contract.statusas === 'nutrauktas' ||
    contract.statusas === 'archyvuotas'
  ) {
    return true;
  }
  const expiry = new Date(contract.galiojIki);
  if (Number.isNaN(expiry.getTime())) return false;
  // Prefer UTC date parts for DATE/TIMESTAMP-at-midnight-UTC values
  const expiryDay = Date.UTC(expiry.getUTCFullYear(), expiry.getUTCMonth(), expiry.getUTCDate());
  const now = new Date();
  const todayDay = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return expiryDay < todayDay;
}

function groupContractsByPartner(list: Contract[]): { key: string; label: string; contracts: Contract[] }[] {
  const map = new Map<string, Contract[]>();
  const order: string[] = [];
  for (const c of list) {
    const k = normalizeCompanyGroupKey(c.partneris);
    if (!map.has(k)) {
      map.set(k, []);
      order.push(k);
    }
    map.get(k)!.push(c);
  }
  return order.map((key) => ({
    key,
    label: key === '__empty__' ? '' : key,
    contracts: map.get(key)!
  }));
}

const Sutartys: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [onlyValidContracts, setOnlyValidContracts] = useState(true);
  const [showMyContracts, setShowMyContracts] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const [stats, setStats] = useState<ContractStats | null>(null);
  const [sortPreset, setSortPreset] = useState<SortPreset>('company');
  const [groupByCompany, setGroupByCompany] = useState(true);
  /** Group keys that are folded (collapsed); absent keys = expanded */
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(() => new Set());

  const canManageContracts = hasPermission('contracts.edit') || hasPermission('contracts.delete');
  const isAdmin = hasPermission('contracts.delete');

  const sortApi = SORT_PRESET_TO_API[sortPreset];

  const fetchContracts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (tabValue === 0) {
        params.append('tipas', 'partneriai');
      } else {
        params.append('tipas', 'vidines');
      }
      if (onlyValidContracts) {
        params.append('onlyValid', 'true');
      } else if (statusFilter) {
        params.append('statusas', statusFilter);
      }
      if (showMyContracts) {
        params.append('assignedToMe', 'true');
      }
      params.append('sortBy', sortApi.sortBy);
      params.append('sortOrder', sortApi.sortOrder);

      const response = await fetch(getApiUrl(`/contracts?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contracts');
      }

      const data = await response.json();
      setContracts(data.contracts);

      // Show expiring contracts warning
      if (data.warnings?.expiringContracts > 0) {
        console.log(`⚠️ ${data.warnings.expiringContracts} contracts expiring within 30 days`);
      }
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch contracts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(getApiUrl('/contracts/stats/overview'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching contract statistics:', err);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchStats();
  }, [tabValue, statusFilter, onlyValidContracts, showMyContracts, sortPreset]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenForm = (contract?: Contract) => {
    setEditingContract(contract || null);
    setFormDialogOpen(true);
  };

  const handleCloseForm = () => {
    setFormDialogOpen(false);
    setEditingContract(null);
  };

  const handleFormSubmit = async () => {
    await fetchContracts();
    await fetchStats();
    handleCloseForm();
  };

  const handleDeleteClick = (contract: Contract) => {
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contractToDelete) return;

    try {
      const response = await fetch(getApiUrl(`/contracts/${contractToDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete contract');
      }

      await fetchContracts();
      await fetchStats();
      setDeleteDialogOpen(false);
      setContractToDelete(null);
    } catch (err) {
      console.error('Error deleting contract:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete contract');
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'aktyvus': return 'success';
      case 'laukiantis': return 'warning';
      case 'pasibaiges':
      case 'nutrauktas': return 'error';
      case 'archyvuotas': return 'default';
      default: return 'default';
    }
  };

  const handleArchiveToggle = async (contract: Contract) => {
    const nextStatus = contract.statusas === 'archyvuotas' ? 'aktyvus' : 'archyvuotas';
    try {
      setError(null);
      const response = await fetch(getApiUrl(`/contracts/${contract.id}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statusas: nextStatus })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to update contract status');
      }
      await fetchContracts();
      await fetchStats();
    } catch (err) {
      console.error('Error updating contract archive status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update contract status');
    }
  };

  const isExpiringSoon = (date: string): boolean => {
    const expiryDate = new Date(date);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiryDate <= thirtyDaysFromNow;
  };

  const filteredContracts = useMemo(
    () =>
      contracts.filter((contract) => {
        const matchesSearch =
          contract.pavadinimas.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contract.sutartiesNumeris.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (contract.partneris && contract.partneris.toLowerCase().includes(searchTerm.toLowerCase()));
        if (!matchesSearch) return false;
        if (onlyValidContracts && isEnded(contract)) return false;
        return true;
      }),
    [contracts, searchTerm, onlyValidContracts]
  );

  const contractGroups = useMemo(
    () => groupContractsByPartner(filteredContracts),
    [filteredContracts]
  );

  const handleGroupByCompanyChange = (checked: boolean) => {
    setGroupByCompany(checked);
    if (checked) {
      setSortPreset('company');
      // Start with every company group folded (collapsed)
      const groups = groupContractsByPartner(filteredContracts);
      setCollapsedGroupKeys(new Set(groups.map((g) => g.key)));
    } else {
      setCollapsedGroupKeys(new Set());
    }
  };

  const toggleContractGroupCollapsed = (groupKey: string) => {
    setCollapsedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    const currentTabType = tabValue === 0 ? 'partneriai' : 'vidines';
    const typeContracts = stats.contractsByType[currentTabType] || 0;
    const typeValue = stats.valueByType[currentTabType] || 0;

    return (
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 2,
        mb: 3 
      }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{typeContracts}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('contracts.totalContracts')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <AssessmentIcon color="info" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">€{typeValue.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('contracts.totalValue')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{stats.expiringContracts}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('contracts.expiringContracts')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderContractsTable = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (filteredContracts.length === 0) {
      return (
        <Box textAlign="center" p={3}>
          <Typography variant="h6" gutterBottom>
            {t('contracts.noContracts')}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('contracts.noContractsDescription')}
          </Typography>
          {canManageContracts && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              sx={{ mt: 2 }}
            >
              {t('contracts.createFirstContract')}
            </Button>
          )}
        </Box>
      );
    }

    const tableColCount =
      6 +
      (tabValue === 0 ? 1 : 0) +
      1 +
      1 +
      1 +
      1 +
      (canManageContracts ? 1 : 0);

    const renderContractRow = (contract: Contract) => (
      <TableRow
        key={contract.id}
        sx={
          isEnded(contract)
            ? {
                backgroundColor: '#ffebee',
                '& .MuiTableCell-root': { backgroundColor: '#ffebee' }
              }
            : {}
        }
      >
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {contract.pavadinimas}
            </Typography>
            {contract.aprasymas && (
              <Typography variant="caption" color="text.secondary">
                {contract.aprasymas.substring(0, 50)}
                {contract.aprasymas.length > 50 ? '...' : ''}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>{contract.sutartiesNumeris}</TableCell>
        <TableCell>
          {contract.sutartymoData
            ? new Date(contract.sutartymoData).toLocaleDateString('lt-LT')
            : '-'}
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            {new Date(contract.galiojIki).toLocaleDateString('lt-LT')}
            {isExpiringSoon(contract.galiojIki) && contract.statusas === 'aktyvus' && (
              <Tooltip title={t('contracts.expiringWarning', { count: 1 })}>
                <WarningIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>€{contract.suma.toLocaleString()}</TableCell>
        <TableCell>
          {contract.kiekis.toLocaleString()} {t(`contracts.quantityTypes.${contract.kiekioTipas}`)}
        </TableCell>
        {tabValue === 0 && <TableCell>{contract.taikomosNuolaidos || '-'}</TableCell>}
        <TableCell>{contract.partneris || '-'}</TableCell>
        <TableCell>
          <Chip
            label={t(`contracts.statuses.${contract.statusas}`)}
            color={getStatusColor(contract.statusas)}
            size="small"
          />
        </TableCell>
        <TableCell>
          {contract.responsiblePerson
            ? `${contract.responsiblePerson.firstName} ${contract.responsiblePerson.lastName}`
            : '-'}
        </TableCell>
        <TableCell>
          {contract.creator
            ? `${contract.creator.firstName} ${contract.creator.lastName}`
            : '-'}
        </TableCell>
        {canManageContracts && (
          <TableCell>
            <Box display="flex" gap={1}>
              <IconButton size="small" onClick={() => handleOpenForm(contract)} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
              {contract.statusas === 'archyvuotas' ? (
                <Tooltip title={t('contracts.unarchive', 'Atkurti')}>
                  <IconButton
                    size="small"
                    onClick={() => handleArchiveToggle(contract)}
                    color="success"
                  >
                    <UnarchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('contracts.archive', 'Archyvuoti')}>
                  <IconButton
                    size="small"
                    onClick={() => handleArchiveToggle(contract)}
                    color="default"
                  >
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isAdmin && (
                <IconButton size="small" onClick={() => handleDeleteClick(contract)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
    );

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('contracts.contractName')}</TableCell>
              <TableCell>{t('contracts.contractNumber')}</TableCell>
              <TableCell>Sutartymo data</TableCell>
              <TableCell>{t('contracts.validUntil')}</TableCell>
              <TableCell>{t('contracts.amount')}</TableCell>
              <TableCell>{t('contracts.quantity')}</TableCell>
              {tabValue === 0 && <TableCell>Taikomos nuolaidos</TableCell>}
              <TableCell>{tabValue === 1 ? 'Įmonė' : t('contracts.partner')}</TableCell>
              <TableCell>{t('contracts.status')}</TableCell>
              <TableCell>Atsakingas asmuo</TableCell>
              <TableCell>{t('contracts.createdBy')}</TableCell>
              {canManageContracts && <TableCell>{t('common.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupByCompany
              ? contractGroups.map((group) => {
                  const isExpanded = !collapsedGroupKeys.has(group.key);
                  return (
                    <React.Fragment key={group.key}>
                      <TableRow
                        hover
                        onClick={() => toggleContractGroupCollapsed(group.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleContractGroupCollapsed(group.key);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-expanded={isExpanded}
                        aria-label={t('contracts.groupToggleAria', {
                          company:
                            group.key === '__empty__'
                              ? t('contracts.uncategorizedCompany')
                              : group.label
                        })}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: 'action.hover',
                          '&:focus-visible': { outline: '2px solid', outlineOffset: -2, outlineColor: 'primary.main' }
                        }}
                      >
                        <TableCell colSpan={tableColCount} sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isExpanded ? (
                              <ExpandMoreIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                            ) : (
                              <ChevronRightIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                            )}
                            <Typography variant="subtitle2" fontWeight={600} component="span">
                              {group.key === '__empty__'
                                ? t('contracts.uncategorizedCompany')
                                : group.label}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                              {t('contracts.groupContractCount', { count: group.contracts.length })}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {isExpanded && group.contracts.map((c) => renderContractRow(c))}
                    </React.Fragment>
                  );
                })
              : filteredContracts.map((c) => renderContractRow(c))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!user) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">
          {t('common.error')}: User not authenticated
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {t('contracts.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('contracts.description')}
              </Typography>
            </Box>
            {canManageContracts && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenForm()}
              >
                {t('contracts.addContract')}
              </Button>
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="contract tabs">
            <Tab 
              label="PARDAVIMO SUTARTYS" 
              icon={<HandshakeIcon />} 
              iconPosition="start" 
            />
            <Tab 
              label="PIRKIMO SUTARTYS" 
              icon={<BusinessIcon />} 
              iconPosition="start" 
            />
          </Tabs>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {renderStatsCards()}

      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap', 
            alignItems: 'center',
            '& > *': { minWidth: { xs: '100%', sm: '250px', md: '200px' } }
          }}>
            <TextField
              size="small"
              placeholder={t('contracts.searchContracts')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            />
            <TextField
              size="small"
              select
              label={t('contracts.filterByStatus')}
              value={onlyValidContracts ? 'aktyvus' : statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={onlyValidContracts}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('contracts.allStatuses')}</MenuItem>
              <MenuItem value="aktyvus">{t('contracts.statuses.aktyvus')}</MenuItem>
              <MenuItem value="pasibaiges">{t('contracts.statuses.pasibaiges')}</MenuItem>
              <MenuItem value="nutrauktas">{t('contracts.statuses.nutrauktas')}</MenuItem>
              <MenuItem value="laukiantis">{t('contracts.statuses.laukiantis')}</MenuItem>
              <MenuItem value="archyvuotas">{t('contracts.statuses.archyvuotas')}</MenuItem>
            </TextField>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="contracts-sort-label">{t('contracts.sortBy')}</InputLabel>
              <Select
                labelId="contracts-sort-label"
                label={t('contracts.sortBy')}
                value={sortPreset}
                onChange={(e) => setSortPreset(e.target.value as SortPreset)}
              >
                <MenuItem value="company">{t('contracts.sort.companyThenExpiry')}</MenuItem>
                <MenuItem value="newest">{t('contracts.sort.newest')}</MenuItem>
                <MenuItem value="expiryAsc">{t('contracts.sort.expiryAsc')}</MenuItem>
                <MenuItem value="expiryDesc">{t('contracts.sort.expiryDesc')}</MenuItem>
                <MenuItem value="number">{t('contracts.sort.contractNumber')}</MenuItem>
                <MenuItem value="title">{t('contracts.sort.contractTitle')}</MenuItem>
              </Select>
            </FormControl>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                minWidth: { xs: '100%', md: 'auto' }
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyValidContracts}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setOnlyValidContracts(checked);
                      if (checked) setStatusFilter('');
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={t('contracts.onlyValidFilter', 'Galiojančios')}
                sx={{ mr: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupByCompany}
                    onChange={(e) => handleGroupByCompanyChange(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={t('contracts.groupByCompany')}
                sx={{ mr: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showMyContracts}
                    onChange={(e) => setShowMyContracts(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={t('contracts.assignedToMeFilter')}
                sx={{ mr: 0 }}
              />
            </Box>
          </Box>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {renderContractsTable()}
          <ContractLogSection contractType="partneriai" />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderContractsTable()}
          <ContractLogSection contractType="vidines" />
        </TabPanel>
      </Paper>

      {/* Contract Form Dialog */}
      <ContractFormDialog
        open={formDialogOpen}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        contract={editingContract}
        contractType={tabValue === 0 ? 'partneriai' : 'vidines'}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('contracts.deleteContract')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('contracts.deleteConfirm')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('contracts.deleteWarning')}
          </Typography>
          {contractToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">
                {contractToDelete.pavadinimas}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contractToDelete.sutartiesNumeris}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Sutartys;
